import type { ApiResponse } from "@/src/types/apiResponse"

import axios from "axios"
import { apiUrls, httpClient as axiosInstance } from "@/src/api"

export type GCSUrlType =
    | "JOINING_CONFIRMATION"
    | "SELECTION_CONFIRMATION"
    | "RESUME"
    | "CANDIDATE_RESUME"
    | "BULK_JOB"
    | "PUBLIC"

const getGCSUrl = async (type: GCSUrlType, fileName: string): Promise<ApiResponse<string>> => {
    try {
        const resp = await axiosInstance.get(
            `${apiUrls.BULK_UPLOAD_PRIVATE_FILE()}?fileName=${fileName}&fileType=${type}`
        )
        return {
            status: "success",
            data: resp?.data?.data?.url,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

const getGCSUrlPublic = async (type: GCSUrlType, fileName: string): Promise<ApiResponse<string>> => {
    try {
        // Targeted fix: Use CANDIDATE_RESUME for job details route
        const isJobDetailsRoute = window.location.pathname.includes("/dashboard/jobDetails/")
        const finalType = isJobDetailsRoute && type === "RESUME" ? "CANDIDATE_RESUME" : type

        const resp = await axiosInstance.get(
            `${apiUrls.BULK_UPLOAD_PUBLIC_RESUME}?fileName=${fileName}&fileType=${finalType}`
        )
        return {
            status: "success",
            data: resp?.data?.data?.url,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

const uploadFileToGCS = async (url: string, file: File): Promise<ApiResponse<string>> => {
    const headers = {
        "Content-Type": "application/octet-stream",
    }

    try {
        const resp = await axios.put(url, file, { headers })
        return {
            status: "success",
            data: resp?.data,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}

export const uploadGCSFile = async (file: File, type: GCSUrlType): Promise<ApiResponse<string>> => {
    const gcsUrlResp = await getGCSUrl(type, file.name)

    if (gcsUrlResp.status === "error") return gcsUrlResp
    if (!gcsUrlResp.data)
        return {
            status: "error",
            errorMessage: "Failed to upload the file",
        }

    const fileUrlResult = gcsUrlResp.data.substring(0, gcsUrlResp.data.indexOf("?"))

    const uploadResult = await uploadFileToGCS(gcsUrlResp.data, file)
    if (uploadResult.status === "error") return uploadResult

    return { status: "success", data: fileUrlResult }
}

export const uploadGCSFilePublic = async (file: File, type: GCSUrlType): Promise<ApiResponse<string>> => {
    const gcsUrlResp = await getGCSUrlPublic(type, file.name)

    if (gcsUrlResp.status === "error") return gcsUrlResp
    if (!gcsUrlResp.data)
        return {
            status: "error",
            errorMessage: "Failed to upload the file",
        }

    const fileUrlResult = gcsUrlResp.data.substring(0, gcsUrlResp.data.indexOf("?"))

    const uploadResult = await uploadFileToGCS(gcsUrlResp.data, file)
    if (uploadResult.status === "error") return uploadResult

    return { status: "success", data: fileUrlResult }
}

export const getGSCFileUrl = async (publicUrl: string, type: GCSUrlType): Promise<ApiResponse<string>> => {
    try {
        const resp = await axiosInstance.get(`${apiUrls.BULK_UPLOAD_FILE_DOWNLOAD()}?fileUrl=${publicUrl}`)
        return {
            status: "success",
            data: resp?.data?.data?.url || resp?.data?.data?.signedUrl,
        }
    } catch (e: any) {
        console.log(e)
        return {
            status: "error",
            errorMessage: e.status,
        }
    }
}
